import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Select from 'react-select';
import {
  HiOutlineCurrencyRupee, HiOutlineRefresh, HiOutlineChevronDown,
  HiOutlineServer, HiOutlineLightningBolt, HiOutlineSearch,
} from 'react-icons/hi';
import {
  getCloudRunServicesList, getCloudRunCosts,
  getAllVMInstances, getBuckets, getCloudBuildTriggersLight,
  getSchedulerJobs, getCloudRunJobs, estimateServiceCosts,
} from '../services/api';
import selectStyles from '../shared/selectStyles';
import './CostEstimation.css';

function getMonthLabel(offset) {
  const d = new Date();
  d.setMonth(d.getMonth() - offset);
  return d.toLocaleString('default', { month: 'long', year: 'numeric' });
}

const MONTH_OPTIONS = Array.from({ length: 6 }, (_, i) => ({
  value: i,
  label: i === 0 ? `${getMonthLabel(0)} (Current)` : getMonthLabel(i),
}));

const SERVICE_TYPES = [
  { value: 'cloud-run', label: 'Cloud Run Services', fetcher: getCloudRunServicesList, nameKey: 'name' },
  { value: 'vm', label: 'VM Instances', fetcher: getAllVMInstances, nameKey: 'name' },
  { value: 'cloud-run-jobs', label: 'Cloud Run Jobs', fetcher: getCloudRunJobs, nameKey: 'name' },
  { value: 'buckets', label: 'Cloud Storage Buckets', fetcher: getBuckets, nameKey: 'name' },
  { value: 'cloud-build', label: 'Cloud Build', fetcher: getCloudBuildTriggersLight, nameKey: 'name' },
  { value: 'scheduler', label: 'Cloud Scheduler', fetcher: getSchedulerJobs, nameKey: 'name' },
];

const SERVICE_TYPE_OPTIONS = SERVICE_TYPES.map(s => ({ value: s.value, label: s.label }));

function formatINR(val) {
  if (val == null || isNaN(val)) return '₹0.00';
  return `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function CostEstimation({ gcpProjects }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [serviceType, setServiceType] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [costResults, setCostResults] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const [expandedSections, setExpandedSections] = useState({ breakdown: true });
  const [error, setError] = useState(null);

  const projectOptions = useMemo(() =>
    gcpProjects.map(p => ({ value: p._id, label: `${p.projectName} (${p.projectId})`, projectName: p.projectName, projectId: p.projectId })),
    [gcpProjects]
  );

  useEffect(() => {
    setServiceType(null);
    setAvailableServices([]);
    setSelectedServices([]);
    setCostResults(null);
    setError(null);
  }, [selectedProject]);

  useEffect(() => {
    setAvailableServices([]);
    setSelectedServices([]);
    setCostResults(null);
    setError(null);

    if (!selectedProject || !serviceType) return;

    const stConfig = SERVICE_TYPES.find(s => s.value === serviceType.value);
    if (!stConfig) return;

    let cancelled = false;
    setLoadingServices(true);

    stConfig.fetcher(selectedProject.value)
      .then(res => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : [];
        setAvailableServices(data);
      })
      .catch(() => {
        if (!cancelled) setAvailableServices([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingServices(false);
      });

    return () => { cancelled = true; };
  }, [selectedProject, serviceType]);

  const serviceOptions = useMemo(() => {
    const stConfig = SERVICE_TYPES.find(s => s.value === serviceType?.value);
    if (!stConfig) return [];
    return availableServices.map(svc => {
      const name = svc[stConfig.nameKey] || svc.id || svc.name || 'unknown';
      let detail = '';
      if (serviceType.value === 'vm') {
        detail = svc.machineType ? ` (${svc.machineType} — ${svc.status || ''})` : '';
      } else if (serviceType.value === 'cloud-run') {
        detail = svc.region ? ` (${svc.region})` : '';
      } else if (serviceType.value === 'buckets') {
        detail = svc.location ? ` (${svc.location})` : '';
      }
      return { value: name, label: `${name}${detail}`, data: svc };
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [availableServices, serviceType]);

  const estimateCosts = useCallback(async () => {
    if (!selectedProject || !serviceType || selectedServices.length === 0) return;

    setEstimating(true);
    setError(null);
    setCostResults(null);

    try {
      if (serviceType.value === 'cloud-run') {
        const names = selectedServices.map(s => s.value);
        const regions = selectedServices.map(s => s.data?.region || '');
        const res = await getCloudRunCosts(selectedProject.value, names, regions, { month: monthOffset });
        const costs = res.data;
        const currentMonth = costs?.currentMonth?.services || {};
        const previousMonth = costs?.previousMonth?.services || {};

        const services = {};
        let totalCostINR = 0;
        for (const [svcName, cur] of Object.entries(currentMonth)) {
          const prev = previousMonth[svcName] || {};
          const cost = cur.grossCostINR || cur.totalCostINR || 0;
          totalCostINR += cost;
          services[svcName] = {
            name: svcName,
            totalCostINR: Math.round(cost * 100) / 100,
            costBreakdown: {
              cpuINR: cur.costBreakdown?.cpuINR || 0,
              memoryINR: cur.costBreakdown?.memoryINR || 0,
              requestsINR: cur.costBreakdown?.requestsINR || 0,
            },
            cpuVcpus: cur.cpuVcpus || 0,
            memGiB: cur.memGiB || 0,
            billableHours: cur.billableInstanceHours || 0,
            requestCount: cur.requestCount || 0,
            previousCostINR: prev.grossCostINR || prev.totalCostINR || 0,
            source: costs.source || 'estimated',
          };
        }

        setCostResults({
          serviceType: 'cloud-run',
          currency: 'INR',
          services,
          totalCostINR: Math.round(totalCostINR * 100) / 100,
          serviceCount: Object.keys(services).length,
          source: costs.source || 'estimated',
          period: getMonthLabel(monthOffset),
        });
      } else if (serviceType.value === 'cloud-run-jobs') {
        const jobNames = selectedServices.map(s => s.value);
        const selectedJobsData = availableServices.filter(j => jobNames.includes(j.name));
        const services = {};
        let totalCostINR = 0;
        for (const job of selectedJobsData) {
          const execCount = job.executionCount || 0;
          const estCost = execCount * 0.50;
          totalCostINR += estCost;
          services[job.name] = {
            name: job.name,
            totalCostINR: Math.round(estCost * 100) / 100,
            costBreakdown: { executionINR: Math.round(estCost * 100) / 100 },
            executionCount: execCount,
            source: 'rough-estimate',
          };
        }
        setCostResults({
          serviceType: 'cloud-run-jobs',
          currency: 'INR',
          services,
          totalCostINR: Math.round(totalCostINR * 100) / 100,
          serviceCount: Object.keys(services).length,
          source: 'rough-estimate',
          period: 'Current Month (Estimated)',
        });
      } else {
        const names = selectedServices.map(s => s.value);
        const res = await estimateServiceCosts(selectedProject.value, serviceType.value, names);
        setCostResults({
          ...res.data,
          period: 'Current Month (Estimated)',
        });
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to estimate costs');
    } finally {
      setEstimating(false);
    }
  }, [selectedProject, serviceType, selectedServices, monthOffset, availableServices]);

  const serviceEntries = useMemo(() => {
    if (!costResults?.services) return [];
    return Object.entries(costResults.services).sort(([, a], [, b]) => (b.totalCostINR || 0) - (a.totalCostINR || 0));
  }, [costResults]);

  const breakdownColumns = useMemo(() => {
    if (!costResults) return [];
    const st = costResults.serviceType;
    if (st === 'cloud-run') return [
      { key: 'cpuINR', label: 'CPU (₹)', color: 'var(--accent-cyan, #06b6d4)' },
      { key: 'memoryINR', label: 'Memory (₹)', color: 'var(--accent-purple, #a855f7)' },
      { key: 'requestsINR', label: 'Requests (₹)', color: 'var(--accent-orange, #f59e0b)' },
    ];
    if (st === 'vm') return [
      { key: 'computeINR', label: 'Compute (₹)', color: 'var(--accent-cyan, #06b6d4)' },
      { key: 'diskINR', label: 'Disk (₹)', color: 'var(--accent-purple, #a855f7)' },
    ];
    if (st === 'buckets') return [
      { key: 'storageINR', label: 'Storage (₹)', color: 'var(--accent-cyan, #06b6d4)' },
    ];
    if (st === 'cloud-build') return [
      { key: 'buildINR', label: 'Build (₹)', color: 'var(--accent-cyan, #06b6d4)' },
    ];
    if (st === 'scheduler') return [
      { key: 'jobINR', label: 'Job (₹)', color: 'var(--accent-cyan, #06b6d4)' },
    ];
    if (st === 'cloud-run-jobs') return [
      { key: 'executionINR', label: 'Execution (₹)', color: 'var(--accent-cyan, #06b6d4)' },
    ];
    return [];
  }, [costResults]);

  const extraInfoColumns = useMemo(() => {
    if (!costResults) return [];
    const st = costResults.serviceType;
    if (st === 'cloud-run') return [
      { key: 'cpuVcpus', label: 'vCPUs' },
      { key: 'memGiB', label: 'Memory (GiB)' },
      { key: 'requestCount', label: 'Requests', format: v => v?.toLocaleString() || '0' },
    ];
    if (st === 'vm') return [
      { key: 'machineType', label: 'Machine Type' },
      { key: 'status', label: 'Status' },
      { key: 'diskTotalGb', label: 'Disk (GB)' },
    ];
    if (st === 'buckets') return [
      { key: 'storageClass', label: 'Storage Class' },
      { key: 'estimatedGB', label: 'Est. Size (GB)' },
      { key: 'totalFiles', label: 'Files' },
    ];
    if (st === 'cloud-build') return [
      { key: 'estimatedMinutesPerMonth', label: 'Est. Min/Month' },
      { key: 'estimatedBuildsPerMonth', label: 'Builds/Month' },
    ];
    if (st === 'scheduler') return [
      { key: 'schedule', label: 'Schedule' },
      { key: 'state', label: 'State' },
    ];
    if (st === 'cloud-run-jobs') return [
      { key: 'executionCount', label: 'Executions' },
    ];
    return [];
  }, [costResults]);

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const isRoughEstimate = costResults && costResults.source !== 'billing' && costResults.serviceType !== 'cloud-run';

  return (
    <div className="cost-page">
      <div className="pv-header">
        <div className="pv-header-left">
          <h1><HiOutlineCurrencyRupee /> Cost Estimation</h1>
          <p className="pv-subtitle">Estimate costs for GCP services across your projects</p>
        </div>
      </div>

      {/* Cascading Filters */}
      <div className="cost-filters">
        <div className="cost-filter-item" style={{ minWidth: 260 }}>
          <label>1. Select Project</label>
          <Select
            styles={selectStyles}
            options={projectOptions}
            value={selectedProject}
            onChange={setSelectedProject}
            placeholder="Choose a project..."
            isClearable
            menuPortalTarget={document.body}
          />
        </div>

        <div className="cost-filter-item" style={{ minWidth: 220 }}>
          <label>2. Service Type</label>
          <Select
            styles={selectStyles}
            options={SERVICE_TYPE_OPTIONS}
            value={serviceType}
            onChange={setServiceType}
            placeholder="Choose type..."
            isDisabled={!selectedProject}
            isClearable
            menuPortalTarget={document.body}
          />
        </div>

        <div className="cost-filter-item cost-filter-multi" style={{ minWidth: 320, flex: 1 }}>
          <label>3. Select Services</label>
          <Select
            styles={selectStyles}
            options={serviceOptions}
            value={selectedServices}
            onChange={val => setSelectedServices(val || [])}
            placeholder={loadingServices ? 'Loading services...' : 'Choose services...'}
            isMulti
            isDisabled={!serviceType || loadingServices}
            isLoading={loadingServices}
            closeMenuOnSelect={false}
            menuPortalTarget={document.body}
          />
        </div>

        {serviceType?.value === 'cloud-run' && (
          <div className="cost-filter-item" style={{ minWidth: 200 }}>
            <label>Period</label>
            <select
              className="cost-month-select"
              value={monthOffset}
              onChange={e => setMonthOffset(parseInt(e.target.value))}
              disabled={estimating}
            >
              {MONTH_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}

        <div className="cost-filter-actions">
          <button
            className="cost-estimate-btn"
            onClick={estimateCosts}
            disabled={estimating || !selectedProject || !serviceType || selectedServices.length === 0}
          >
            {estimating ? (
              <><HiOutlineRefresh size={16} className="spin" /> Estimating...</>
            ) : (
              <><HiOutlineSearch size={16} /> Estimate Costs</>
            )}
          </button>
        </div>
      </div>

      {/* Step indicators */}
      {!selectedProject && (
        <div className="cost-step-hint">
          <span className="cost-step-number">1</span>
          Select a GCP project to get started
        </div>
      )}
      {selectedProject && !serviceType && (
        <div className="cost-step-hint">
          <span className="cost-step-number">2</span>
          Choose a service type to view available services
        </div>
      )}
      {serviceType && selectedServices.length === 0 && !loadingServices && (
        <div className="cost-step-hint">
          <span className="cost-step-number">3</span>
          Select one or more services, then click "Estimate Costs"
        </div>
      )}

      {/* Loading indicator */}
      {estimating && (
        <div className="cost-loading">
          <div className="loading-bar"><div className="loading-bar-inner" /></div>
          <span>Calculating cost estimates...</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="cost-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Results */}
      {costResults && !estimating && (
        <>
          {/* Summary Cards */}
          <div className="cost-summary-cards">
            <div className="cost-card">
              <div className="cost-card-icon" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                <HiOutlineCurrencyRupee size={22} />
              </div>
              <div className="cost-card-body">
                <span className="cost-card-value">{formatINR(costResults.totalCostINR)}</span>
                <span className="cost-card-label">Total Estimated Cost</span>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-card-icon" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                <HiOutlineServer size={22} />
              </div>
              <div className="cost-card-body">
                <span className="cost-card-value">{costResults.serviceCount}</span>
                <span className="cost-card-label">Services Selected</span>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-card-icon" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                <HiOutlineLightningBolt size={22} />
              </div>
              <div className="cost-card-body">
                <span className={`cost-source-badge ${isRoughEstimate ? 'rough' : costResults.source === 'billing' ? 'billing' : 'estimated'}`}>
                  {isRoughEstimate ? 'Rough Estimate' : costResults.source === 'billing' ? 'Exact (Billing)' : 'Estimated (Metrics)'}
                </span>
                <span className="cost-card-label">Data Source</span>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-card-icon" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>
                <HiOutlineCurrencyRupee size={22} />
              </div>
              <div className="cost-card-body">
                <span className="cost-card-value">{costResults.period}</span>
                <span className="cost-card-label">Period</span>
              </div>
            </div>
          </div>

          {/* Rough Estimate Disclaimer */}
          {isRoughEstimate && (
            <div className="cost-disclaimer">
              These estimates use published GCP list prices and approximate usage data.
              Actual costs may differ based on sustained-use discounts, CUDs, or free tier credits.
            </div>
          )}

          {/* Cost Breakdown Table */}
          <div className="cost-section">
            <div className="cost-section-header" onClick={() => toggleSection('breakdown')}>
              <div className="cost-section-left">
                <HiOutlineServer size={18} style={{ color: 'var(--accent-green, #22c55e)' }} />
                <span className="cost-section-title">Cost Breakdown by Service</span>
                <span className="cost-section-count">{serviceEntries.length}</span>
              </div>
              <HiOutlineChevronDown size={16} className={`cost-chevron ${expandedSections.breakdown ? 'expanded' : ''}`} />
            </div>
            {expandedSections.breakdown && (
              <div className="cost-table-wrap">
                <table className="cost-table">
                  <thead>
                    <tr>
                      <th>Service</th>
                      {extraInfoColumns.map(col => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                      {breakdownColumns.map(col => (
                        <th key={col.key} className="cost-col-right">{col.label}</th>
                      ))}
                      <th className="cost-col-right">Total (₹)</th>
                      {costResults.serviceType === 'cloud-run' && (
                        <th className="cost-col-right">Prev Month (₹)</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {serviceEntries.map(([svcName, svc]) => (
                      <tr key={svcName} className="cost-table-row">
                        <td className="cost-col-name-svc">{svcName}</td>
                        {extraInfoColumns.map(col => (
                          <td key={col.key} className="cost-col-muted">
                            {col.format ? col.format(svc[col.key]) : (svc[col.key] ?? '—')}
                          </td>
                        ))}
                        {breakdownColumns.map(col => (
                          <td key={col.key} className="cost-col-right" style={{ color: col.color }}>
                            {formatINR(svc.costBreakdown?.[col.key])}
                          </td>
                        ))}
                        <td className="cost-col-right cost-col-inr">{formatINR(svc.totalCostINR)}</td>
                        {costResults.serviceType === 'cloud-run' && (
                          <td className="cost-col-right cost-col-muted">{formatINR(svc.previousCostINR)}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="cost-total-row">
                      <td className="cost-col-name-svc">Total</td>
                      {extraInfoColumns.map(col => <td key={col.key}></td>)}
                      {breakdownColumns.map(col => (
                        <td key={col.key} className="cost-col-right" style={{ color: col.color }}>
                          {formatINR(serviceEntries.reduce((s, [, v]) => s + (v.costBreakdown?.[col.key] || 0), 0))}
                        </td>
                      ))}
                      <td className="cost-col-right cost-col-inr">{formatINR(costResults.totalCostINR)}</td>
                      {costResults.serviceType === 'cloud-run' && (
                        <td className="cost-col-right cost-col-muted">
                          {formatINR(serviceEntries.reduce((s, [, v]) => s + (v.previousCostINR || 0), 0))}
                        </td>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="cost-footer-note">
            {costResults.source === 'billing'
              ? 'Costs from GCP Billing Export (BigQuery) — matches GCP Console billing.'
              : costResults.serviceType === 'cloud-run'
                ? 'Costs estimated from GCP Cloud Monitoring allocation metrics. For exact costs, configure GCP_BILLING_TABLE in .env.'
                : 'Costs estimated using published GCP list prices. Actual costs may vary.'}
          </div>
        </>
      )}

      {/* Empty state when no results and not estimating */}
      {!costResults && !estimating && !error && selectedProject && serviceType && selectedServices.length > 0 && (
        <div className="cost-empty">
          <HiOutlineCurrencyRupee size={40} />
          <h3>Ready to estimate</h3>
          <p>Click "Estimate Costs" to calculate costs for {selectedServices.length} selected service(s)</p>
        </div>
      )}
    </div>
  );
}

export default CostEstimation;
